import { format } from "date-fns";

/** Subset of EventDateDto fields the public listings need for series rendering. */
export interface SeriesAwareRow {
  isSeries?: boolean | null;
  seriesId?: number | null;
  eventName?: string | null;
  eventStartDateTimeLocal?: Date | string | null;
  seriesEndDateTimeLocal?: Date | string | null;
}

/** URL-friendly form of the event name for the cosmetic name segment of the series URL. */
export function slugifyEventName(name?: string | null): string {
  const slug = (name ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "event";
}

/** Series purchase URL: the id is the lookup key, the name segment is cosmetic. */
export function seriesPurchasePath(
  seriesId: number,
  eventName?: string | null,
): string {
  return `/series/${seriesId}/${slugifyEventName(eventName)}`;
}

/** Target URL when a series-card click should redirect, or null for ordinary rows. */
export function seriesNavigationTarget(row: SeriesAwareRow): string | null {
  if (!row.isSeries || row.seriesId == null) return null;
  return seriesPurchasePath(row.seriesId, row.eventName);
}

export function formatSeriesDateRange(row: SeriesAwareRow): string {
  const start = row.eventStartDateTimeLocal;
  const end = row.seriesEndDateTimeLocal;
  if (!start) return "";

  const startDate = typeof start === "string" ? new Date(start) : start;
  if (!end) return format(startDate, "EEEE, MMMM do");

  const endDate = typeof end === "string" ? new Date(end) : end;
  if (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  ) {
    return format(startDate, "EEEE, MMMM do, yyyy");
  }

  if (startDate.getFullYear() === endDate.getFullYear()) {
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${format(startDate, "MMMM d")} – ${format(endDate, "d, yyyy")}`;
    }
    return `${format(startDate, "MMMM d")} – ${format(endDate, "MMMM d, yyyy")}`;
  }
  return `${format(startDate, "MMMM d, yyyy")} – ${format(endDate, "MMMM d, yyyy")}`;
}

export function seatsRemainingLabel(isSeries?: boolean | null): string {
  return isSeries ? "Seats remaining · all dates" : "Seats remaining";
}
export function seatsSoldLabel(isSeries?: boolean | null): string {
  return isSeries ? "Seats sold · all dates" : "Seats sold";
}

export function seatsAllDatesTooltip(
  isSeries?: boolean | null,
): string | undefined {
  return isSeries
    ? "Totaled across every date in the series. A multi-day pass counts once per date (admission slots), so it isn't a headcount of unique buyers."
    : undefined;
}
